import {
  Bell,
  FileClock,
  GitBranch,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Map,
  Menu,
  Settings,
  ShieldCheck,
  UserCog,
  X,
} from "lucide-react";
import { useIsFetching, useIsMutating, useQuery } from "@tanstack/react-query";
import { useState, type PropsWithChildren } from "react";
import { NavLink } from "react-router-dom";
import { useApi } from "../api";
import type { User } from "../types";
import { usePermissions } from "../permissions";
import { businessRoleLabels } from "@payangel/shared";

const nav = [
  ["/", "Dashboard", LayoutDashboard],
  ["/initiatives", "Initiatives", ListChecks],
  ["/products", "Product register", ListChecks],
  ["/dependencies", "Dependencies", GitBranch],
  ["/roadmap", "Roadmap", Map],
  ["/governance", "Governance", ShieldCheck],
  ["/executive", "Executive", FileClock],
  ["/reports", "Reports", FileClock],
] as const;

export function AppShell({
  children,
  user,
  onLogout,
}: PropsWithChildren<{ user: User; onLogout(): Promise<void> }>) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const api = useApi();
  const permissions = usePermissions();
  const networkBusy = useIsFetching() + useIsMutating() > 0;
  const notifications = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ unread: number }>("/notifications"),
    refetchInterval: 60_000,
  });
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      {networkBusy && (
        <div
          className="global-progress"
          role="progressbar"
          aria-label="Updating application data"
        >
          <span />
        </div>
      )}
      <header className="topbar">
        <button
          className="icon-button mobile-menu"
          aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
        <NavLink
          to="/"
          className="topbar-brand"
          onClick={() => setMobileOpen(false)}
        >
          <span className="brand-logo-wrap">
            <img
              className="brand-logo"
              src="/payangel-logo.png"
              alt="PayAngel"
            />
          </span>
          <span className="brand-name">Strategic Prioritisation</span>
        </NavLink>
        <span className="topbar-sub">2026 portfolio governance</span>
        <span className="topbar-pill">INTERNAL</span>
        <NavLink
          className="topbar-ico"
          to="/notifications"
          aria-label={`${notifications.data?.unread ?? 0} unread notifications`}
        >
          <Bell size={17} />
          <span className="notif-dot">{notifications.data?.unread ?? 0}</span>
        </NavLink>
        <div className="user-menu" title={user.email}>
          <span className="avatar">{initials(user.displayName)}</span>
          <span className="user-copy">
            <strong>{user.displayName}</strong>
            <small>
              {permissions.businessAccess
                ? permissions.businessAccess.roles
                    .map((r) => businessRoleLabels[r])
                    .join(" · ")
                : user.isSuperAdmin
                  ? "super administrator"
                  : user.role}
            </small>
          </span>
        </div>
        <button
          className="topbar-logout"
          type="button"
          aria-label="Sign out"
          title="Sign out"
          onClick={() => void onLogout()}
        >
          <LogOut size={16} />
        </button>
      </header>
      <div className="alert-bar" role="status">
        <ShieldCheck size={15} />
        <span>
          Scores, lifecycle changes, exports, and security events are
          automatically audited.
        </span>
      </div>
      <nav
        className={`nav ${mobileOpen ? "mobile-open" : ""}`}
        aria-label="Primary navigation"
      >
        {nav
          .filter(([to]) =>
            to === "/governance"
              ? permissions.can("governance.view")
              : ["/executive", "/reports"].includes(to)
                ? permissions.can("executive.view")
                : true,
          )
          .map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `nav-tab ${isActive ? "active" : ""}`
              }
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={16} /> <span>{label}</span>
            </NavLink>
          ))}
        {(permissions.nativeCapacity ||
          permissions.can("capacity.view") ||
          permissions.can("integrations.manage")) && (
          <NavLink
            to="/capacity"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            onClick={() => setMobileOpen(false)}
          >
            <LayoutDashboard size={16} />
            <span>Capacity</span>
          </NavLink>
        )}
        {permissions.can("audit.view") && (
          <NavLink
            to="/admin/audit"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            onClick={() => setMobileOpen(false)}
          >
            <FileClock size={16} /> <span>Audit</span>
          </NavLink>
        )}
        {permissions.can("roles.manage") && (
          <NavLink
            to="/admin/access"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            onClick={() => setMobileOpen(false)}
          >
            <UserCog size={16} /> <span>User access</span>
          </NavLink>
        )}
        <span className="nav-spacer" />
        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
          onClick={() => setMobileOpen(false)}
        >
          <Settings size={16} /> <span>Settings</span>
        </NavLink>
      </nav>
      <main id="main-content" className="view-container">
        {children}
      </main>
    </div>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}
